import { useState, useEffect } from 'react'
import { configApi } from '../services/configApi'
import { ConfigType, ConfigReference } from '../types/config'

export interface ClientJobContext {
  /** Customer-level Context ("table_analysis_prompt") config value. */
  customerContext: string
  /** Job-level Context config value (the job's own contribution only). */
  jobContext: string
  isLoading: boolean
}

// Pull the value contributed at one specific level out of a merged-config
// audit trail, so the About tab can show the customer's and the job's context
// separately instead of the concatenated merge.
function sourceValueAt(sources: ConfigReference[] | undefined, level: ConfigType): string {
  const src = sources?.find((s) => s.source_config?.config_type === level)
  return typeof src?.value === 'string' ? src.value : ''
}

/**
 * Loads the customer- and job-level "Context" config (the descriptions the
 * Settings → About tab shows under Client & Job Context, #973 item 7). These
 * are edited in Admin → Context; this hook is read-only.
 */
export default function useClientJobContext(customer?: string, job?: string, enabled = true): ClientJobContext {
  const [customerContext, setCustomerContext] = useState('')
  const [jobContext, setJobContext] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !customer) return
    // The merged-config route is /configs/:customer/:job/merged and Fiber does
    // not match an empty :job segment — a customer-only request always 404s.
    // Skip the fetch entirely; if customer-level context is ever wanted here,
    // the backend needs a real customer-scoped route first.
    if (!job) return
    let cancelled = false
    setIsLoading(true)
    configApi
      .getMergedConfig(customer, job, { config_type: ConfigType.Job, customer, job })
      .then((result) => {
        if (cancelled) return
        const sources = result.fields?.['table_analysis_prompt']?.sources
        setCustomerContext(sourceValueAt(sources, ConfigType.Customer))
        setJobContext(sourceValueAt(sources, ConfigType.Job))
      })
      .catch(() => {
        // Non-fatal: the About tab just shows no descriptions.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [customer, job, enabled])

  return { customerContext, jobContext, isLoading }
}
